import type { BuildState } from '@/types';
import { getComponents } from '@/lib/calculations';
import { Card } from '@/components/ui/Card';

interface WeightBreakdownProps {
  state: BuildState;
  totalWeight: number;
}

/**
 * Per-component contribution chart. Each row shows a colored swatch,
 * the label, the absolute grain weight, the percentage of total,
 * and a horizontal bar sized to the percentage.
 */
export function WeightBreakdown({ state, totalWeight }: WeightBreakdownProps) {
  const components = getComponents(state);

  return (
    <Card padding="lg">
      <h2 className="font-semibold mb-4">Weight Breakdown</h2>

      <div className="space-y-2">
        {components.map((c) => {
          const pct = (c.weight / totalWeight) * 100;
          return (
            <div key={c.label} className="flex items-center gap-3">
              <div
                className="w-2 h-6 rounded-sm shrink-0"
                style={{ background: c.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between text-sm">
                  <span>{c.label}</span>
                  <span className="font-mono tabular-nums">
                    <span className="text-zinc-100">{c.weight.toFixed(1)}</span>{' '}
                    <span className="text-zinc-500">gr · {pct.toFixed(1)}%</span>
                  </span>
                </div>
                <div className="h-1 mt-1 bg-zinc-800 rounded">
                  <div
                    className="h-1 rounded transition-all duration-200"
                    style={{ width: `${pct}%`, background: c.color }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="pt-3 mt-3 border-t border-zinc-800 flex justify-between font-mono text-sm">
        <span className="font-semibold">TOTAL</span>
        <span className="font-semibold text-ember-500 tabular-nums">
          {totalWeight.toFixed(1)} gr
        </span>
      </div>
    </Card>
  );
}
